import React, { useEffect, useState } from "react";
import api from "../api/axios";
import { useAuth } from "../auth/AuthContext";
import { FaPlus } from "react-icons/fa";
import { Loader2 } from "lucide-react";
import UiText from "../components/UiText";
import AddCompanyModal from "../components/AddCompanyModal";
import CompanyDetailsPanel from "./CompanyDetailsPanel.js";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../components/ui/ToastContext";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import useDebounce from "../utils/useDebounce";

// Industries for deal manager
import "./CompaniesPage.css";

const INDUSTRIES = [
  "Technology",
  "Finance",
  "Healthcare",
  "Retail",
  "Manufacturing",
  "Real Estate",
  "Education",
  "Telecommunications",
  "Energy",
  "Transportation",
  "Hospitality",
  "Media & Entertainment",
  "Legal Services",
  "Consulting",
  "Insurance",
  "Agriculture",
  "Construction",
  "Government",
  "Non-profit",
  "Other",
];

export default function CompaniesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();

  const [companies, setCompanies] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [industryFilter, setIndustryFilter] = useState("all");
  const [error, setError] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [companyToEdit, setCompanyToEdit] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  // React Hook Form with Zod validation
  // Removed: Now handled by AddCompanyModal component

  // Server-side search/filter/pagination: only the current page of matching
  // companies is ever fetched, instead of the entire collection. This is what
  // keeps the page fast as the number of companies grows into the thousands.
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/companies/", {
        params: {
          skip: (currentPage - 1) * itemsPerPage,
          limit: itemsPerPage,
          search: debouncedSearch || undefined,
          industry: industryFilter !== "all" ? industryFilter : undefined,
        },
      });
      setCompanies(res.data);
      const total = Number(res.headers?.["x-total-count"]);
      setTotalCount(Number.isFinite(total) ? total : res.data.length);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch companies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [currentPage, itemsPerPage, debouncedSearch, industryFilter]);

  // Deep-link support: if we navigated here with a specific companyId (e.g.
  // from a notification) that isn't on the currently loaded page, fetch it
  // directly instead of only searching the current page's list.
  useEffect(() => {
    const companyIdFromState = location.state?.companyId;
    if (!companyIdFromState) return;

    const found = companies.find((c) => String(c.id) === String(companyIdFromState));
    if (found) {
      setSelectedCompany(found);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/api/companies/${companyIdFromState}`);
        setSelectedCompany(res.data);
      } catch (err) {
        console.error("Failed to load linked company", err);
      }
    })();
  }, [location.state, companies]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  // Static industry list (already defined above) used for the filter dropdown
  // now that we no longer hold the full company list on the client.
  const industryOptions = INDUSTRIES;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, industryFilter]);

  const openAddModal = () => {
    setCompanyToEdit(null);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setCompanyToEdit(null);
  };

  const handleCompanySaved = async () => {
    await fetchCompanies();
    setShowAddModal(false);
    setCompanyToEdit(null);
    toast.success(companyToEdit ? "Company updated successfully." : "Company added successfully.");
  };

  const confirmDeleteCompany = (company) => {
    setCompanyToDelete(company);
    setShowDeleteModal(true);
  };

  const handleDeleteCompany = async () => {
    try {
      await api.delete(`/api/companies/${companyToDelete.id}`);
      if (selectedCompany?.id === companyToDelete.id) {
        setSelectedCompany(null);
      }
      setShowDeleteModal(false);
      setCompanyToDelete(null);
      toast.success(`"${companyToDelete.name}" was deleted.`);
      await fetchCompanies();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete company.");
    }
  };

  const openEditModal = (company) => {
    setCompanyToEdit(company);
    setShowAddModal(true);
  };

  const handleViewCustomers = (company) => {
    if (!company?.id) return;
    navigate("/customers", {
      state: {
        companyId: company.id,
        companyName: company.name,
      },
    });
  };

  const handleViewDeals = (company) => {
    if (!company?.id) return;
    navigate("/deals", {
      state: {
        companyId: company.id,
        companyName: company.name,
      },
    });
  };

  return (
    <div style={{ padding: "2rem" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1.5rem",
        }}
      >
        <div>
          <h1 style={{ fontSize: "2rem", fontWeight: "700", margin: 0 }}>
            Companies
          </h1>
          <p style={{ color: "#6b7280", marginTop: "4px" }}>
            Manage your business relationships
          </p>
        </div>

        {user?.role !== 'user' && (
        <button
          onClick={openAddModal}
          style={{
            background: "#2563eb",
            color: "white",
            padding: "10px 18px",
            borderRadius: "8px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            border: "none",
            cursor: "pointer",
            height: "44px",
          }}
        >
          <FaPlus style={{ fontSize: "12px", verticalAlign: "middle" }} /> Add Company
        </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-xl shadow-sm mb-8">
        <div className="flex gap-4 items-center">
          <input
            type="text"
            placeholder="Search companies..."
            className="w-full border border-gray-300 rounded-xl bg-white cursor-text text-gray-700 shadow-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            value={industryFilter}
            onChange={(e) => setIndustryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl bg-white text-gray-700 shadow-sm cursor-pointer"
            title="Industry"
          >
            <option value="all">All Industries</option>
            {industryOptions.map((ind) => (
              <option key={ind} value={ind}>
                {ind}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* TABLE + DETAILS PANEL */}
      <div className="grid grid-cols-3 gap-6">
        {/* LEFT - Company Table */}
        <div className="col-span-2 bg-white rounded-xl shadow-sm p-6">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="pb-3 pt-2">
                  <UiText variant="section" as="span">Name</UiText>
                </th>
                <th className="pb-3 pt-2">
                  <UiText variant="section" as="span">Industry</UiText>
                </th>
                <th className="pb-3 pt-2">
                  <UiText variant="section" as="span">Location</UiText>
                </th>
                <th className="pb-3 pt-2">
                  <UiText variant="section" as="span">Customers</UiText>
                </th>
                <th className="pb-3 pt-2">
                  <UiText variant="section" as="span">Website</UiText>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" style={{ padding: "2rem", textAlign: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
                      <Loader2 size={32} color="#3b82f6" style={{ animation: "spin 1s linear infinite" }} />
                      <span style={{ color: "#64748b", fontSize: "0.95rem" }}>Loading companies...</span>
                    </div>
                    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="5">{error}</td>
                </tr>
              ) : companies.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: "2rem", textAlign: "center", color: "#9ca3af" }}>
                    {user?.role === 'user' ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", color: "#9ca3af" }}>
                        <div style={{ fontSize: "1rem", fontWeight: 600 }}>You have not been collabrated yet</div>
                        <div style={{ fontSize: "0.95rem" }}>Kindly contact Administrator</div>
                      </div>
                    ) : (
                      <div>No companies found.</div>
                    )}
                  </td>
                </tr>
              ) : (
                companies.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedCompany(c)}
                    className={`border-t cursor-pointer transition-all hover:bg-gray-50 ${
                      selectedCompany?.id === c.id ? "bg-blue-50 ring-2 ring-inset ring-blue-400" : ""
                    }`}
                  >
                    <td className="py-4">
                      <UiText variant="section" as="span" className="font-semibold text-gray-900">{c.name}</UiText>
                    </td>
                    <td className="py-4">
                      <UiText variant="small" as="span">{c.industry || "-"}</UiText>
                    </td>
                    <td className="py-4">
                      <UiText variant="small" as="span">{c.location || "-"}</UiText>
                    </td>
                    <td className="py-4">
                      <span className="inline-flex items-center justify-center bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                        {c.customers?.length || 0}
                      </span>
                    </td>
                    <td className="py-4">
                      {c.website ? (
                        <a
                          href={c.website}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline text-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <UiText variant="small" as="span" className="text-blue-600 hover:underline">
                            {c.website.length > 25 ? c.website.substring(0, 25) + '...' : c.website}
                          </UiText>
                        </a>
                      ) : (
                        <UiText variant="small" as="span">-</UiText>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* PAGINATION CONTROLS */}
          {totalCount > 0 && (
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "1.5rem",
              paddingTop: "1rem",
              borderTop: "1px solid #e5e7eb",
            }}>
              {/* Left: Items per page */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <UiText variant="small" as="span">Show:</UiText>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "0.4rem 0.6rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "13px",
                    cursor: "pointer",
                    background: "#fff",
                  }}
                >
                  <option value={6}>6</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <UiText variant="small" as="span" style={{ color: "#6b7280" }}>
                  of {totalCount} companies
                </UiText>
              </div>

              {/* Right: Page navigation */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: "0.4rem 0.8rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    background: currentPage === 1 ? "#f3f4f6" : "#fff",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: currentPage === 1 ? "#9ca3af" : "#374151",
                  }}
                >
                  Previous
                </button>

                <div style={{ display: "flex", gap: "0.25rem" }}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        style={{
                          padding: "0.4rem 0.7rem",
                          border: currentPage === pageNum ? "2px solid #2563eb" : "1px solid #d1d5db",
                          borderRadius: "6px",
                          background: currentPage === pageNum ? "#eff6ff" : "#fff",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: currentPage === pageNum ? "600" : "500",
                          color: currentPage === pageNum ? "#2563eb" : "#374151",
                          minWidth: "32px",
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: "0.4rem 0.8rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    background: currentPage === totalPages ? "#f3f4f6" : "#fff",
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: currentPage === totalPages ? "#9ca3af" : "#374151",
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT - Company Details Panel */}
        <div className="bg-white rounded-xl shadow-sm p-4 col-span-1">
          <CompanyDetailsPanel
            company={selectedCompany}
            onEdit={(company) => openEditModal(company)}
            onDelete={(company) => confirmDeleteCompany(company)}
            onAddDeal={(company) => {
              // Navigate to Deals page with filter or open deal creation modal
              window.location.href = `/deals?company=${company.id}`;
            }}
            onAddNote={(company) => {
              // Open quick note modal or navigate to activities
              toast.info(`Quick note for ${company.name} - Feature coming soon. Use Activity tab in panel.`);
            }}
            onViewCustomers={handleViewCustomers}
            onViewDeals={handleViewDeals}
          />
        </div>
      </div>

      {/* ADD COMPANY MODAL */}
      {showAddModal && (
        <AddCompanyModal
          onClose={closeAddModal}
          onSuccess={handleCompanySaved}
          companyToEdit={companyToEdit}
        />
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-96">
            <UiText variant="heading" as="h2">Delete Company</UiText>
            <UiText variant="small" as="p" className="mb-6">
              Are you sure you want to delete{" "}
              <strong>{companyToDelete?.name}</strong>? This action cannot be undone.
            </UiText>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-lg border"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCompany}
                className="px-4 py-2 rounded-lg bg-red-600 text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
